/**
=========================================================
* STAIL - Brand Gradients (Glassmorphism Theme)
=========================================================
* Centralized gradient definitions for consistent theming
* Updated: December 2, 2025 - Phase 4A (Glassmorphism)
=========================================================
*/

/**
 * Brand gradient definitions used throughout the application.
 * Unified glassmorphism theme with navy (#0a1628) and gold (#f4c542).
 * Darker navy gradients for page elements, gold for accents and buttons.
 * 
 * Usage:
 * import { brandGradients } from 'assets/theme/base/gradients';
 * sx={{ background: brandGradients.navyGlass }}
 */
const brandGradients = {
  // PRIMARY GLASSMORPHISM GRADIENTS (Navy Theme)
  // Main background gradient (matches Frontpage)
  navyGlass: "linear-gradient(135deg, #0a1628 0%, #1a2942 50%, #0d1f3c 100%)",
  
  // Darker variant for page headers and elevated elements
  navyGlassDark: "linear-gradient(135deg, #050b14 0%, #0d1628 50%, #0a1628 100%)",
  
  // Medium variant for cards and containers
  navyGlassMedium: "linear-gradient(135deg, #0d1628 0%, #152138 50%, #0f1a2e 100%)",
  
  // GOLD ACCENT GRADIENTS (Primary Actions)
  // Primary button gradient (matches Frontpage login button)
  goldGradient: "linear-gradient(135deg, #f4c542 0%, #f39c12 100%)",
  
  // Gold variant for hover states
  goldGradientHover: "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)",
  
  // Subtle gold for backgrounds
  goldSubtle: "linear-gradient(135deg, rgba(244, 197, 66, 0.15) 0%, rgba(243, 156, 18, 0.15) 100%)",
  
  // SEMANTIC COLOR GRADIENTS (Keep for important actions)
  // Success actions (green)
  greenSuccess: "linear-gradient(135deg, #00630f 0%, #00a020 100%)",
  
  // Error/Delete actions (red)
  redError: "linear-gradient(135deg, #630000 0%, #a00000 100%)",
  
  // Warning actions (orange-gold blend)
  orangeWarning: "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)",
  
  // LEGACY GRADIENTS (Deprecated - mapped to new theme)
  // These exist for backward compatibility but use new colors
  brownOrange: "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)", // → orangeWarning
  pinkMagenta: "linear-gradient(135deg, #0d1628 0%, #1a2942 100%)", // → navyGlass
  blue: "linear-gradient(135deg, #0d1628 0%, #152138 100%)", // → navyGlassMedium
  greenLight: "linear-gradient(135deg, #00630f 0%, #00a020 100%)", // → greenSuccess
  yellowGreen: "linear-gradient(135deg, #f4c542 0%, #f39c12 100%)", // → goldGradient
  orangeBrown: "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)", // → orangeWarning
  goldBrown: "linear-gradient(135deg, #f4c542 0%, #f39c12 100%)", // → goldGradient
  purpleMagenta: "linear-gradient(135deg, #0d1628 0%, #1a2942 100%)", // → navyGlass
  
  // Gold/yellow variations (updated to match new theme)
  goldYellow: "linear-gradient(135deg, #f4c542 0%, #f39c12 100%)",
  goldYellowDark: "linear-gradient(135deg, #e67e22 0%, #d35400 100%)",
  bluePurple: "linear-gradient(135deg, #0d1628 0%, #1a2942 100%)",
  navyBlue: "linear-gradient(135deg, #0a1628 0%, #1a2942 50%, #0d1f3c 100%)",
  
  // Tab/Active state gradients (navy with glow effect)
  pinkMagentaActive: "linear-gradient(135deg, #0d1628 0%, #1a2942 50%, #0d1628 100%)",
  greenLightActive: "linear-gradient(135deg, #00630f 0%, #00a020 50%, #00630f 100%)",
  goldBrownActive: "linear-gradient(135deg, #f4c542 0%, #f39c12 50%, #f4c542 100%)",
  yellowGreenActive: "linear-gradient(135deg, #f4c542 0%, #f39c12 50%, #f4c542 100%)",
  
  // Loading/animation gradient (gold theme)
  loadingBar: "linear-gradient(90deg, #f4c542 0%, #f39c12 100%)",
};

/**
 * Semantic gradient mappings for common UI patterns.
 * Unified glassmorphism theme - all elements use navy/gold palette.
 */
export const semanticGradients = {
  // Dialog headers (unified dark navy)
  dialogDefault: brandGradients.navyGlassDark,
  dialogSuccess: brandGradients.greenSuccess,
  dialogWarning: brandGradients.orangeWarning,
  dialogError: brandGradients.redError,
  dialogSpecial: brandGradients.goldGradient,
  
  // Buttons (gold for primary, navy for secondary)
  buttonPrimary: brandGradients.goldGradient,
  buttonSecondary: brandGradients.navyGlassMedium,
  buttonSuccess: brandGradients.greenSuccess,
  buttonDanger: brandGradients.redError,
  buttonWarning: brandGradients.orangeWarning,
  
  // Page headers (unified dark navy)
  pageHeader: brandGradients.navyGlassDark,
  
  // Status indicators (gold for active)
  statusActive: brandGradients.goldBrownActive,
  statusSuccess: brandGradients.greenLightActive,
  statusPending: brandGradients.yellowGreenActive,
};

export default brandGradients;
